import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Table from "widgets/controls/tables/genericTable";
import defineAtTablecolumns, {
  AuditTrailPageTableModel,
} from "models/tables/auditTrailPage";
import { getAuditTrailFilters } from "store/page/pageSelectors";
import {
  AuditTrailPageFilters,
  setAuditTrailPageFilters,
} from "store/page/pageSlice";
import { auditPage } from "api/audit";
import { SearchInput } from "widgets/shared/inputs/search";
import DateRangePickerField from "widgets/shared/inputs/dateRangePicker";
import { formatDateToZ } from "util/datelib";
import Combobox from "widgets/shared/inputs/combobox";
import { ReferenceDropdown } from "models/dropdowns/reference";
import AuditTrailDialog from "widgets/dialogs/auditTrail";
import { SecondaryHeightLayout } from "hooks/viewport-dimensions";
import { useTranslation } from "react-i18next";

const AuditTrail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const columns = defineAtTablecolumns((info) => {
    const row = info.row.original;
    setSelected(row);
    setIsOpen(true);
  });

  // Get initial filters from store once
  const initialFilters = useSelector(getAuditTrailFilters);

  // Local state for filters - initialized once from Redux
  const [filters, setFilters] = useState<AuditTrailPageFilters>(
    () => initialFilters
  );

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState<AuditTrailPageTableModel>();

  const fetchData = useCallback(async () => {
    const {
      paginationState,
      sortingState,
      search,
      dateFrom,
      dateTo,
      reference,
    } = filters;
    setIsLoading(true);
    const { data, error, isLoading } = await auditPage({
      pageIndex: paginationState.pageIndex + 1,
      pageSize: paginationState.pageSize,
      order: sortingState[0].id,
      desc: sortingState[0].desc,
      search: search,
      dateFrom: dateFrom,
      dateTo: dateTo,
      reference: reference,
    });
    setIsLoading(isLoading);
    if (!error) {
      setDataSource(data.data || []);

      setFilters((prevFilters) => ({
        ...prevFilters,
        paginationState: {
          pageIndex: data.currentPage - 1,
          pageSize: paginationState.pageSize,
        },
        totCount: data.totalCount,
        pageCount: data.totalPages,
      }));

      dispatch(
        setAuditTrailPageFilters({
          paginationState: { pageIndex: 0, pageSize: 10 },
          sortingState: [{ desc: true, id: "created" }],
          totCount: 0,
          pageCount: 0,
          search: search,
          dateFrom: dateFrom,
          dateTo: dateTo,
          reference: reference,
        })
      );
    }
  }, [
    filters.paginationState.pageIndex,
    filters.paginationState.pageSize,
    filters.sortingState,
    filters.search,
    filters.dateFrom,
    filters.dateTo,
    filters.reference,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClose = () => {
    setSelected(null);
    setIsOpen(false);
  };

  return (
    <>
      {isOpen && (
        <AuditTrailDialog
          isOpen={isOpen}
          jsonData={selected}
          onClose={handleClose}
          onSuccess={handleClose}
        />
      )}
      <SecondaryHeightLayout>
        <Table
          filterbar={
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
              <div className="flex-1 lg:flex-grow-[1]">
                <Combobox
                  id="device-select-status"
                  placeholder={t("support.audit.trail.select.reference")}
                  variant="solid"
                  color="secondary"
                  value={filters.reference}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      reference: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={ReferenceDropdown}
                  labelKey="label"
                  valueKey="value"
                  sizes="md"
                  rounded="rounded-xl"
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <DateRangePickerField
                  id={"date-range-picker-audit"}
                  sizes="md"
                  rounded="rounded-xl"
                  variant="solid"
                  color="secondary"
                  defaultValue={
                    filters.dateFrom && filters.dateTo
                      ? [filters.dateFrom, filters.dateTo]
                      : undefined
                  }
                  onChange={(val) => {
                    let from: Date | null | undefined = undefined;
                    let to: Date | null | undefined = undefined;

                    if (Array.isArray(val)) {
                      [from, to] = val;
                    }

                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      dateFrom: from ? formatDateToZ(from, false) : undefined,
                      dateTo: to ? formatDateToZ(to, true) : undefined,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <SearchInput
                  onChange={(filters) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      search: filters,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  type="text"
                  value={filters.search}
                  sizes="md"
                  rounded="rounded-xl"
                  color="secondary"
                  disabled={isLoading}
                />
              </div>
            </div>
          }
          noDataLabel={t("support.audit.trail.no.data")}
          loading={isLoading}
          onRowClick={() => {}}
          data={dataSource}
          columns={columns}
          pagination={filters.paginationState}
          paginationOptions={{
            onPaginationChange: (pagination) => {
              const page =
                typeof pagination === "function"
                  ? pagination(filters.paginationState)
                  : pagination;
              setFilters((prevFilters) => ({
                ...prevFilters,
                paginationState: page,
              }));
            },
            rowCount: filters.totCount,
            pageCount: filters.pageCount,
          }}
          sorting={filters.sortingState}
          onSortingChange={(updaterOrValue) => {
            const newSortingState =
              typeof updaterOrValue === "function"
                ? updaterOrValue(filters.sortingState)
                : updaterOrValue;
            if (newSortingState && newSortingState.length > 0) {
              setFilters((prevFilters) => ({
                ...prevFilters,
                sortingState: newSortingState,
              }));
            } else {
              setFilters((prevFilters) => ({
                ...prevFilters,
                sortingState: [{ id: "name", desc: false }],
              }));
            }
          }}
        />
      </SecondaryHeightLayout>
    </>
  );
};

export default AuditTrail;
